import { useEffect, useState } from "react";
import { api } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ArrowDownToLine, ArrowUpFromLine, History, Loader2 } from "lucide-react";

const fmtQty = (n) => Number(n ?? 0).toLocaleString("id-ID", { maximumFractionDigits: 2 });
const fmtTgl = (t) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(t || ""));
  if (m) return `${m[3]}/${m[2]}/${m[1]}`;
  const d = new Date(t);
  return isNaN(d) ? "-" : `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
};

// Popup riwayat masuk/keluar saat kartu gudang diklik di slide Stok Gudang
export function GudangRiwayatDialog({ barang, onOpenChange }) {
  const [riwayat, setRiwayat] = useState(null);

  useEffect(() => {
    if (!barang) return;
    setRiwayat(null);
    api.get(`/barang/${barang.id}/transaksi`)
      .then((res) => setRiwayat(res.data || []))
      .catch(() => setRiwayat([]));
  }, [barang]);

  return (
    <Dialog open={!!barang} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="gudang-riwayat-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 pr-6">
            <History className="h-5 w-5 text-emerald-600" /> Riwayat Barang
          </DialogTitle>
          <DialogDescription>
            <span className="font-mono">{barang?.kode}</span> · {barang?.nama}
          </DialogDescription>
        </DialogHeader>
        {barang && (
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-slate-50 border border-slate-200 px-4 py-2">
              <p className="text-[10px] uppercase tracking-wider text-slate-400">Stok Saat Ini</p>
              <p className={`text-xl font-extrabold tabular-nums ${barang.menipis ? "text-rose-600" : "text-slate-900"}`}
                data-testid="gudang-riwayat-stok">
                {fmtQty(barang.stok_akhir)}<span className="text-xs font-semibold text-slate-400 ml-1">{barang.satuan || ""}</span>
              </p>
            </div>
            <span className={`rounded-full px-2.5 py-1 text-[10px] font-bold border ${
              barang.menipis ? "bg-rose-50 text-rose-600 border-rose-200" : "bg-emerald-50 text-emerald-600 border-emerald-200"}`}>
              {barang.menipis ? "MENIPIS" : "AMAN"}
            </span>
          </div>
        )}
        {riwayat === null ? (
          <div className="py-8 flex justify-center" data-testid="gudang-riwayat-loading">
            <Loader2 className="h-5 w-5 animate-spin text-emerald-600" />
          </div>
        ) : riwayat.length === 0 ? (
          <p className="text-sm text-slate-400 py-4 text-center" data-testid="gudang-riwayat-empty">
            Belum ada transaksi keluar/masuk untuk barang ini.
          </p>
        ) : (
          <div className="space-y-1.5 max-h-72 overflow-y-auto pr-1" data-testid="gudang-riwayat-list">
            {riwayat.map((t) => (
              <div key={t.id} className="flex items-center gap-2 rounded-md bg-white border border-slate-100 px-2.5 py-1.5"
                data-testid={`gudang-riwayat-item-${t.id}`}>
                {t.tipe === "MASUK"
                  ? <ArrowDownToLine className="h-3.5 w-3.5 text-emerald-600 shrink-0" />
                  : <ArrowUpFromLine className="h-3.5 w-3.5 text-rose-600 shrink-0" />}
                <div className="min-w-0 flex-1">
                  <p className="text-xs text-slate-800 truncate">
                    <span className={`font-semibold ${t.tipe === "MASUK" ? "text-emerald-700" : "text-rose-700"}`}>
                      {t.tipe === "MASUK" ? "Masuk" : "Keluar"} {fmtQty(t.qty)}{t.satuan ? ` ${t.satuan}` : ""}
                    </span>
                    {t.tipe === "MASUK"
                      ? (t.pengirim ? ` · ${t.pengirim}` : "")
                      : (t.pengambil ? ` · ${t.pengambil}` : "")}
                  </p>
                  {t.keterangan && <p className="text-[10px] text-slate-400 truncate">{t.keterangan}</p>}
                </div>
                <span className="text-[10px] text-slate-400 shrink-0">{fmtTgl(t.tanggal)}</span>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
